import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputAdornment,
    TextField,
    Typography
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Cookies from 'js-cookie';

import logo from '../../files/images/logo.png';
import { useLocation, useNavigate } from 'react-router-dom';
import ForgotPassword from '../../components/forgot-password';
import { useToast } from '../../providers/toaster-provider';

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";
import LoadingOverlay from '../../components/loading-overlay';
import { useUser } from '../../providers/user-provider';

// Carousel slides data
const carouselSlides = [
    {
        img: 'https://img.pikbest.com/ai/illus_our/20230424/0a526cebefaaebee70b593e14b81761e.jpg!bw700',
        caption: 'Track your finances with ease',
    },
    {
        img: 'https://images.pexels.com/photos/1533483/pexels-photo-1533483.jpeg?cs=srgb&dl=light-dawn-landscape-1533483.jpg&fm=jpg',
        caption: 'Visualize your spending habits',
    },
    {
        img: 'https://machinelearningknowledge.ai/wp-content/uploads/2024/05/image2.jpg',
        caption: 'Plan for a better future',
    },
];

export const Carousel: React.FC = () => {
    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3500,
        arrows: false,
        pauseOnHover: true,
        accessibility: false
    };
    return (
        <Box maxWidth={500} width="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <Box width="100%">
                <Slider {...settings}>
                {carouselSlides.map((slide, i) => (
                    <Box key={i} display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
                    <img
                        src={slide.img}
                        alt={slide.caption}
                        style={{ width: 500, height: 600, borderRadius: 24, marginBottom: 16 }}
                    />
                    </Box>
                ))}
                </Slider>
            </Box>
        </Box>
    );
};


const AdminLogin: React.FC = () => {
    const { showError } = useToast();
    const { refreshUser } = useUser();
    const location = useLocation();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [forgotStep, setForgotStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<boolean>(false);

    const [login, setLogin] = useApi(); 

    useEffect(() => {
        document.title = "FineAnts Admin | Login";
    }, []);

    useEffect(() => {
        const { data, isLoading, error } = login;
        if (data && !isLoading) {
            Cookies.set('token', data.token, { expires: remember ? 30 : undefined });
            Cookies.set('user', JSON.stringify(data.user), { expires: remember ? 30 : undefined });
            refreshUser();
        }
        if (error) {
            showError('Login Failed: Incorrect email or password.');
            setLoading(false);
            setError(true);
        }
    }, [login, remember]);
    
    const handleLogin = () => {
        const { url, method } = buildApiRequestConfig(routes.user.login);
        setLogin({ url, method, body: { email, password, isAdmin: true } });
        setError(false);
        setLoading(true);
    };

    return (
        <Box width={'100vw'} height={'100vh'} bgcolor="var(--color-page-background)">
        <Box display="flex" minHeight="100vh" bgcolor="var(--color-page-background)" maxWidth={1200} margin="0 auto">
        <LoadingOverlay isLoading={loading} title="Logging in..." message="Please wait while we log you in." />
        {/* Left Side */}
        <Box 
            flex={1} 
            display="flex" 
            flexDirection="column" 
            justifyContent="center" 
            alignItems="center" 
            px={8}
        >
            <Box width="100%" mb={6} mt={-6} display="flex" alignItems="center">
                <img src={logo} alt="FineAnts" style={{ height: 32, marginRight: 8 }} />
            </Box>
            <Box width="100%" maxWidth={400}>
                {(location.pathname === '/admin/forgot-password') ? (
                    <ForgotPassword
                        email={email}
                        setEmail={setEmail}
                        onBack={() => { navigate('/admin'); setForgotStep(1); }}
                        step={forgotStep}
                        setStep={setForgotStep}
                        socialLogin={null}
                    />
                    ) : (
                    <>
                        <Typography variant="h4" fontWeight={600} mb={1}>
                            Login
                        </Typography>
                        <Typography color="text.secondary" mb={3}>
                            Login to access your FineAnts account
                        </Typography>
                        <TextField
                            label="Email"
                            placeholder="john.doe@gmail.com"
                            fullWidth
                            margin="normal"
                            value={email}
                            error={!!error}
                            onChange={e => setEmail(e.target.value)}
                            InputProps={{ style: { background: 'var(--color-page-background)' } }}
                        />
                        <TextField
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            margin="normal"
                            value={password}
                            error={!!error}
                            onChange={e => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(s => !s)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                style: { background: 'var(--color-page-background)' }
                            }}
                        />
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={2}>
                            <FormControlLabel
                            control={<Checkbox checked={remember} onChange={e => setRemember(e.target.checked)} />}
                            label={<Typography fontSize={14}>Remember me</Typography>}
                            />
                            <Typography color="#FF8682" fontSize={14} sx={{ cursor: 'pointer' }} onClick={() => navigate('/admin/forgot-password')}>
                                Forgot Password
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            fullWidth
                            sx={{ bgcolor: 'var(--color-primary)', color: '#fff', fontWeight: 600, py: 1.2, borderRadius: 1, boxShadow: 'none', mb: 1, mt: 1 }}
                            onClick={handleLogin}
                        >
                            Login
                        </Button>
                    </>
                    )}
                </Box>
            </Box>
            {/* Right Side - Carousel */}
            <Box flex={1} display="flex" alignItems="center" justifyContent="center" padding="0 64px">
                <Carousel />
            </Box>
        </Box>
        </Box>
    );
};

export default AdminLogin;
